import type { FeatureContext } from './featureContext';
import { initArenaGump } from '../arenaGump';
import { initArenaHud } from '../arenaHud';
import { initArenaDraftOverlay, initArenaResultsOverlay } from '../arenaOverlays';
import {
  initArenaWarning,
  ARENA_ELITE_WARNING_TEXT,
  ARENA_BOSS_WARNING_TEXT,
} from '../arenaWarning';
import { createArenaDanger } from '../arenaDanger';
import { createComboTracker } from '../comboTracker';
import { initArenaCombo } from '../arenaCombo';
import { volleyFacing, isArenaBossBody, type ArenaDraftOption } from '../sim/arena';
import { ARENA_CLASS_IDS, ARENA_CLASSES } from '../sim/arenaClasses';
import { isMonsterBody } from '../sim/content';
import { rollBloodScatter, bloodSplatCount } from '../sim/blood';
import {
  arenaEliteCue,
  arenaBossCue,
  arenaLevelUpFanfare,
  arenaXpCue,
  arenaHeartbeatCue,
  arenaVacuumCue,
  arenaComboStingerCue,
} from '../audio/arenaCue';
import { monsterCue, spellCue } from '../audio/combatCue';
import { arenaMusicTier } from '../audio/musicDirector';
import { SHAKE_HIT, SHAKE_ELITE, SHAKE_BOSS } from '../render/screenShake';
import { BURST_PARTICLES, BOSS_BURST_PARTICLES } from '../render/arenaFx';
import { openContextMenu } from '../ignoreMenu';
import { proximityCueVolume } from './cueVolume';

/**
 * Arena juice colours (Arena Slice F, PRD #334, ADR-0178; volley FX ride the
 * ArenaFx cascade, ADR-0179). Damage the runner DEALS floats gold (distinct from
 * the red the runner TAKES, which keeps the duel red - unchanged outside the
 * arena); the enemy-death burst is an ember orange, the boss-death burst
 * Blackthorn purple.
 */
const ARENA_DEALT_COLOR = 0xffd24d;
const ARENA_BURST_COLOR = 0xffa03c;
const ARENA_BOSS_BURST_COLOR = 0xc45cff;

// Loudness ceilings for the bestiary monster cues (ADR-0187), proximity-scaled per
// creature like the vortex gust: the attack swing and death cry mix like the vortex/
// rat cues; the hurt yelp sits below (a maxed volley lands dozens of hits). The
// monster's move/idle voice is the SoundObserver's (MONSTER_MOVE_VOLUME).
const MONSTER_ATTACK_VOLUME = 0.7;
const MONSTER_DIE_VOLUME = 0.7;
const MONSTER_HURT_VOLUME = 0.5;
// Minimum ms between monster hurt yelps, shared across the whole cascade: a maxed
// volley is ~100 hits in a couple of seconds, so unthrottled yelps would white-noise.
// One yelp per beat-ish reads as "the wave is being hurt" without drowning the mix.
const MONSTER_HURT_COOLDOWN_MS = 250;
// The death cries and attack swings need the same shared throttle: one volley
// cascade can down 10+ monsters on the same tick (and a surround is many bites a
// beat), and their `ActionAnimation`s land in one flush - unthrottled, the
// simultaneous cues sum into one painful blast. First-wins per window, like the
// hurt yelp: one cry reads as "the wave is dying" without the pile-up.
const MONSTER_DIE_COOLDOWN_MS = 250;
const MONSTER_ATTACK_COOLDOWN_MS = 250;
// The runner's own spell FX (ADR-0187): fixed volume (their own magic, the swing-
// grunt rationale - not positioned against the listener), throttled like the hurt
// yelps because a maxed cascade fires dozens of flights/impacts in a beat.
const SPELL_FX_VOLUME = 0.6;
const SPELL_FX_COOLDOWN_MS = 150;

/** Injectable seams for unit tests; defaults are the real modules. */
export interface ArenaInstallDeps {
  initArenaGump?: typeof initArenaGump;
  initArenaHud?: typeof initArenaHud;
  initArenaDraftOverlay?: typeof initArenaDraftOverlay;
  initArenaResultsOverlay?: typeof initArenaResultsOverlay;
  initArenaWarning?: typeof initArenaWarning;
  initArenaCombo?: typeof initArenaCombo;
}

/**
 * The Arena (PRD #334 + the juice/feedback PRDs #372/#378/#430/#431/#432): the
 * Arena Master gump + join menu, the owner-only run HUD / draft / results
 * overlays, the elite/boss warning + low-HP danger + kill-streak combo machines,
 * the volley FX cascade with its staged sounds/blood, and the arena music
 * override. Everything is gated on the local player's own run (owner-only pushes
 * / the `arenaActive` latch), so town presentation is untouched.
 */
export function installArena(ctx: FeatureContext, deps: ArenaInstallDeps = {}): void {
  // The Arena Master gump (ADR-0245): opened by double-clicking the Arena Master
  // NPC (the Bank Escort's double-click precedent). It shows the top `?board=arena`
  // runs (reusing the shared leaderboard fetcher), a Join button that starts a run,
  // and an inert placeholder shop. It is an independent in-world window (NOT in the
  // toolbar one-panel group), so it may coexist with the leaderboard window; the
  // long-press "Join the arena" menu stays as an additional affordance.
  const arenaGump = (deps.initArenaGump ?? initArenaGump)({
    fetchPage: ctx.fetchLeaderboardPage,
    viewerName: () => ctx.viewerName(),
    // A normal player's join carries no `classId` (the default-class entry, exactly
    // like the long-press "Join the arena" row); the server rules authoritatively on
    // entry, so this is fire-and-forget with no client-side rejection handling.
    onJoinArena: (challengeId) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'StartArenaRun', entity: playerId, challengeId });
      }
    },
    requestHistory: (before) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) ctx.client()?.requestArenaHistory(playerId, before);
    },
  });
  ctx.events.on('arenaHistoryPage', (msg) => arenaGump.historyPage(msg));

  // The Arena run HUD (Slice E, ADR-0177): the event boards' top-centre band, but
  // private - fed by the owner-only ArenaRunStatus pushes: the survival clock
  // (ticked down between pushes from the render loop, frozen while a draft pauses
  // the run), health, level + XP, kills/wave, and the equipped build.
  const arenaHud = (deps.initArenaHud ?? initArenaHud)();

  // The client's build mirror for the XP-orb magnet (PRD #431 Slice A): the
  // renderer classifies a gem removal against the runner's *effective* pickup
  // radius, which grows with drafted `magnet` levels. The client sees every offer
  // and sends every pick, so it counts its own accepted `magnet` picks and feeds
  // the renderer - presentation only (a wrong guess is a cosmetic mislabel, never
  // wrong XP, per ADR-0215). Reset per run and on every Welcome.
  let arenaDraftOptions: ArenaDraftOption[] = [];
  let arenaMagnetLevel = 0;

  // The Arena level-up draft overlay (ADR-0177): shown on each owner-only
  // ArenaDraft push while the run is server-paused; clicking an option (or the
  // 1/2/3 keys) sends the PickArenaOption command and hides - the server applies
  // the pick and resumes (or immediately re-offers the next draft). An invalid
  // pick leaves the pause and the offer standing server-side, so nothing here
  // needs to handle rejection.
  const arenaDraft = (deps.initArenaDraftOverlay ?? initArenaDraftOverlay)({
    onPick: (index) => {
      const playerId = ctx.playerId();
      if (playerId === undefined) return;
      // A magnet pick lands the moment it's accepted (the `xp-gain` precedent),
      // so grow the client mirror before the server's resume push arrives.
      const picked = arenaDraftOptions[index];
      if (picked?.kind === 'stat' && picked.stat === 'magnet') {
        arenaMagnetLevel += 1;
        ctx.renderer()?.setMagnetLevel(arenaMagnetLevel);
      }
      ctx.client()?.sendCommand({ kind: 'PickArenaOption', entity: playerId, index });
    },
  });

  // The Arena results overlay (ADR-0177): the run's final tally, shown on the one
  // owner-only ArenaResults push. Dismissal is purely visual - by the time it
  // shows, the server has already settled the run and returns the player to the
  // town portal on its own clock.
  const arenaResults = (deps.initArenaResultsOverlay ?? initArenaResultsOverlay)();

  // The Arena elite/boss warning banner + level-up draft veil (Slice F, ADR-0178 /
  // ADR-0218): transient juice chrome, aged on the render loop's sync and reset on
  // every Welcome (the PR #243 guard - a mid-run disconnect forfeits with no push
  // that would ever hide them).
  const arenaWarning = (deps.initArenaWarning ?? initArenaWarning)();

  // The Arena low-HP danger machine (PRD #430 Slice D, ADR-0227): the pure
  // edge-triggered heartbeat throttle, fed the owner-only run-status pushes and
  // polled for a due beat every render frame.
  const arenaDanger = createArenaDanger();

  // The Arena kill-streak combo (PRD #432): the pure, clock-injected counter
  // machine + its `#arena-combo` display. Fed one `recordKill` per enemy die clip,
  // aged + reconciled every render frame, reset on every Welcome / run edge.
  const combo = createComboTracker();
  const arenaCombo = (deps.initArenaCombo ?? initArenaCombo)();

  // Last-seen run active state (Slice E, ADR-0177), the storm latch's twin but
  // owner-only: edge-detects the arena music override off the ArenaRunStatus active
  // flag. `undefined` until the first push (which is the entry push, so the first
  // edge is always the run starting).
  let arenaActive: boolean | undefined;
  // Which modifiers' keystones this run has already celebrated (PRD #378).
  const arenaKeystones = new Set<string>();
  // Shared throttle clocks for the bestiary attack/die/hurt/spell cues: whole-
  // cascade, not per-monster - the point is capping the *mix*, not each voice.
  let lastMonsterAttackMs = -Infinity;
  let lastMonsterDieMs = -Infinity;
  let lastMonsterHurtMs = -Infinity;
  let lastSpellFxMs = -Infinity;
  let lastArrowHitMs = -Infinity;
  ctx.events.on('connection', () => {
    arenaActive = undefined;
    arenaKeystones.clear();
    lastMonsterAttackMs = -Infinity;
    lastMonsterDieMs = -Infinity;
    lastMonsterHurtMs = -Infinity;
    lastSpellFxMs = -Infinity;
    lastArrowHitMs = -Infinity;
  });

  // Reset the Arena run UI on every (re)connection (ADR-0177): a mid-run
  // disconnect forfeits the run server-side and the fresh connection never sends
  // the final active:false push, so the HUD/overlays would otherwise strand over
  // the town view - and the arena music override would play on forever. v1 never
  // resumes a run, so this is unconditionally safe.
  ctx.events.on('welcome', () => {
    arenaHud.reset();
    arenaDraft.reset();
    arenaResults.reset();
    arenaWarning.reset();
    // The client magnet mirror (PRD #431 Slice A) resets with every Welcome too:
    // a fresh connection has drafted nothing.
    arenaMagnetLevel = 0;
    ctx.renderer()?.setMagnetLevel(0);
    // Back to the unseeded state (ADR-0227): the fresh connection's first status
    // observation is a seed again - silent even if it lands mid-danger.
    arenaDanger.reset();
    // Clear the kill-streak combo (PRD #432): a mid-run disconnect forfeits with
    // no run-end push, so drop the chain, the best, and the counter unconditionally.
    combo.reset();
    arenaCombo.reset();
    // The run HUD is gone, so hand the bottom-centre slot back to the town strip
    // (a mid-run disconnect forfeits with no active:false push to restore it).
    ctx.state.setHudSuppressed(false);
    ctx.music.setArena(false);
    // The warm-set twin of the music reset: a mid-run disconnect never sends the
    // final active:false push, so drop the arena warm set with the Welcome too.
    void ctx.audio.warmMode(null);
  });

  ctx.onRendererBuilt((renderer) => {
    // Double-clicking the Arena Master opens the gump (ADR-0245) - an additional
    // affordance beside the long-press "Join the arena" menu. Pure client UI; the
    // gump owns its own fetch/Join wiring, so this just opens it.
    renderer.onArenaMasterUse = () => arenaGump.open();
    // XP-orb absorb plink (arena VS-style orbs): the renderer fires once per frame
    // in which orb clusters reached the runner; each firing is one pitch-varied
    // plink, so a pile pouring in over a few frames becomes a little glittering
    // arpeggio. Purely client-derived - no wire message.
    renderer.onXpAbsorb = () => {
      ctx.audio.play(arenaXpCue());
    };
    // Vacuum grab whoosh (PRD #431 Slice C): the renderer fires once when the
    // runner claims a vacuum pickup and the whole gem field starts its inrush -
    // one full-volume cue, on top of the per-gem arrival plinks above. Purely
    // client-derived from the pickup's snapshot removal (no wire message).
    renderer.onVacuumGrab = () => {
      ctx.audio.play(arenaVacuumCue());
    };
  });

  // The Arena Master's long-press menu (Arena Slice D/E, ADR-0176/0177/0208): the
  // plate-armoured herald NPC. His whole menu is "Join the arena" - exclusive, no
  // Ignore/match rows. Keyed off the replicated `arenaMaster` marker, NOT his
  // name/body (the isStormBody/justice lesson), so a player who reserves the name
  // "Arena Master" can never grow a join row. The server rules on the entry itself
  // (mid-duel / dead / storm-sealed / crowded all refuse with a herald).
  ctx.menu.addEntityMenu((target, req) => {
    if (target.entity.arenaMaster !== true) return null;
    // "Join the arena" enters the arena. Class selection is an admin-only WIP
    // (PRD #446): a normal player's row enters directly as the default class
    // (no `classId` on the wire), while an admin's row opens the class list -
    // one row per registered class, each sending `StartArenaRun` with its
    // `classId`. `isAdminViewer` only reveals the picker; the server
    // re-authorizes in `handleStartArenaRun` (a forged non-admin pick fights
    // as the default class regardless).
    return {
      exclusive: true,
      items: [
        {
          label: 'Join the arena',
          onSelect: () => {
            if (!ctx.state.isAdminViewer()) {
              const playerId = ctx.playerId();
              if (playerId !== undefined) {
                ctx.client()?.sendCommand({ kind: 'StartArenaRun', entity: playerId });
              }
              return;
            }
            openContextMenu({
              x: req.clientX,
              y: req.clientY,
              items: ARENA_CLASS_IDS.map((classId) => ({
                label: `Fight as the ${ARENA_CLASSES[classId].name}`,
                onSelect: () => {
                  const playerId = ctx.playerId();
                  if (playerId !== undefined) {
                    ctx.client()?.sendCommand({ kind: 'StartArenaRun', entity: playerId, classId });
                  }
                },
              })),
            });
          },
        },
      ],
    };
  });

  // The Arena run status pushes (Slice D -> the HUD, ADR-0176/0177): owner-only,
  // pushed on entry, on every beat/bite, on a heartbeat between, and one final
  // active:false at run end. The HUD mirrors the latest push and the arena music
  // override engages/disengages on the active edge (the storm-status precedent).
  ctx.events.on('arenaRunStatus', (msg) => {
    arenaHud.update(msg);
    const runnerId = ctx.playerId();
    if (runnerId !== undefined) {
      ctx.renderer()?.setArenaRunnerHealth(runnerId, msg.hp, msg.maxHp, msg.active);
    }
    // The low-HP danger channel (PRD #430 Slice D, ADR-0227): the same push that
    // painted the health bar drives the vignette breathing and the heartbeat's
    // edge-triggered throttle. Every leave path is one release: a heal above the
    // threshold observes 'none', and death / run end both arrive as the final
    // active:false push (a disconnect-forfeit releases at the next Welcome's reset).
    if (msg.active) {
      arenaWarning.setDanger(arenaDanger.observe(msg.hp, msg.maxHp) === 'danger');
    } else {
      arenaDanger.reset();
      arenaWarning.setDanger(false);
    }
    // The run HUD takes over the bottom-centre slot the town progression strip
    // owns (PRD #334 follow-up), so suppress that strip while a run is live and
    // restore it on the final active:false push - the two never share the band.
    ctx.state.setHudSuppressed(msg.active);
    // Defensive: the run cannot normally end while a draft is outstanding (the
    // pause freezes the horde), but never leave a draft modal over a dead run.
    if (!msg.active) arenaDraft.hide();
    // A level-up tick sends the draft AND a trailing status in the same flush
    // (the beat that rolled the draft is itself a change). update() re-anchors
    // and unfreezes, so re-assert the freeze while the draft modal is still up -
    // the run clock is frozen server-side for exactly that window.
    arenaHud.setPaused(arenaDraft.isOpen());
    if (msg.active !== arenaActive && (arenaActive !== undefined || msg.active)) {
      ctx.music.setArena(msg.active);
      // Warm the Arena-critical sound set on the run edge (issue #626): entry
      // decodes the spell/shot/orb cues ahead of their first required use, and
      // leaving releases the set back to ordinary LRU-evictable residency.
      void ctx.audio.warmMode(msg.active ? 'arena' : null);
    }
    // The intensity ramp (after the engage edge, which resets to tier 1): every
    // active push re-derives the tier from the run clock. The player records it and
    // the director applies it at the next track boundary - the run always opens on
    // the base mid and ramps up through the mp3 remixes as the horde escalates.
    if (msg.active) ctx.music.setArenaTier(arenaMusicTier(msg.elapsedTicks));
    if (msg.active !== arenaActive) {
      // A run ending drops any lingering banner/flash with it (Slice F, ADR-0178);
      // entrance FX rides the server's ArenaWarning beat signal.
      arenaKeystones.clear();
      // The client magnet mirror resets with the run (PRD #431 Slice A): a fresh
      // run drafts its magnet from zero, so the pickup-radius guess must start at
      // the base disc again.
      arenaMagnetLevel = 0;
      ctx.renderer()?.setMagnetLevel(0);
      if (!msg.active) arenaWarning.reset();
      // The kill-streak combo (PRD #432): a fresh run clears the last run's chain
      // AND best; a run ending only hides the counter (the machine keeps
      // bestThisRun for the results overlay's line, cleared on the next start).
      if (msg.active) combo.reset();
      arenaCombo.reset();
    }
    arenaActive = msg.active;
    // Keystone-unlock fanfare (PRD #378 story 15): the replicated build is the
    // exactly-once signal - a modifier newly wearing its keystone flag means the
    // transcendence just landed (a run always starts keystone-less, so a fresh
    // run's first push celebrates nothing).
    for (const entry of msg.modifiers) {
      if (!entry.keystone || arenaKeystones.has(entry.id)) continue;
      arenaKeystones.add(entry.id);
      const playerId = ctx.playerId();
      if (msg.active && !ctx.ui.hidden && playerId !== undefined) {
        ctx.renderer()?.playKeystoneFanfare(playerId);
      }
    }
  });

  // The pending Arena level-up draft (ADR-0176/0177): show the modal; the run is
  // paused server-side (world frozen + walks rejected) so it can sit as long as the
  // runner thinks. Freeze the HUD clock too - the server sends no status pushes
  // during the pause, so extrapolating would invent time the run is not spending.
  ctx.events.on('arenaDraft', (msg) => {
    arenaHud.setPaused(true);
    // Remember the offer so onPick can tell a magnet pick from any other (the
    // client magnet mirror, PRD #431 Slice A).
    arenaDraftOptions = msg.options;
    arenaDraft.show(msg);
    // The level-up moment (ADR-0218): the draft push is the exactly-once level-up
    // signal (a banked multi-level pick re-offers, each its own genuine level), and
    // the server holds the run paused so the beat is readable. Three one-shots fire
    // off it here - the gold rings + burst on the runner, the HUD level pop, and
    // the rising plink-arpeggio fanfare - while the black draft veil is driven
    // continuously from the draft's open state in the render loop
    // (arenaWarning.setVeil), so it fades in with this show and fades out on
    // whichever path closes the draft (pick, re-offer, run end).
    const playerId = ctx.playerId();
    if (playerId !== undefined) ctx.renderer()?.playLevelUpFlourish(playerId);
    arenaHud.celebrate();
    for (const note of arenaLevelUpFanfare()) {
      window.setTimeout(() => ctx.audio.play(note.cue), note.delayMs);
    }
  });

  // The run's final tally (ADR-0176/0177), sent once on the ending tick. The HUD
  // hides on the final status push in the same flush; dismissal is purely visual.
  ctx.events.on('arenaResults', (msg) => {
    arenaDraft.hide();
    // Append the client-tracked peak combo (PRD #432 Slice C) from the combo
    // machine's bestThisRun - a purely local, cosmetic line (no wire field). The
    // machine is not reset on the run-end status push, so the peak is still
    // available here; the next run start clears it.
    arenaResults.show(msg, combo.state(performance.now()).bestThisRun);
  });

  // A new all-time Arena high score was set (ADR-0210): the bank herald's
  // announcement floats in on the town-only Speech path; this broadcast reaches
  // every client (town and arena) purely to sound the one-shot victory sting, so
  // the setter - still mid-run - and the whole bank celebrate together.
  ctx.events.on('arenaRecord', () => {
    ctx.music.playVictory();
  });

  // Arena elite/boss entrance FX (Slice F, ADR-0178), driven off the server's
  // explicit beat-derived signal instead of body-id sniffing in the snapshot loop.
  // The server sends exactly one ArenaWarning per `elite-spawn`/`boss-spawn` beat,
  // so no client-side dedup is needed, and the `warning` kind - not the spawned
  // body - decides elite vs boss. This is what makes progressive elites work: 12
  // distinct elite bodies all read as 'elite', and elite #12 (which reuses the
  // boss's Demon Knight body 0x13E) still never fires the boss FX, nor vice-versa.
  // The herald system line rides its own path; this is the banner+shake+sting only.
  // `arenaActive` guards town (a run is a private owner-only world anyway).
  ctx.events.on('arenaWarning', (msg) => {
    if (arenaActive !== true) return;
    if (msg.warning === 'elite') {
      const eliteText = msg.name ? `An elite ${msg.name} tears into the arena!` : ARENA_ELITE_WARNING_TEXT;
      arenaWarning.warn(eliteText, 'elite');
      ctx.renderer()?.kickShake(SHAKE_ELITE);
      ctx.audio.play(arenaEliteCue());
    } else {
      // The boss banner shouts the villain's name (Blackthorn, Minax, Exodus, ...),
      // server-supplied off the beat; the static line is the fallback.
      const bossText = msg.name ? `${msg.name.toUpperCase()} ENTERS THE ARENA` : ARENA_BOSS_WARNING_TEXT;
      arenaWarning.warn(bossText, 'boss');
      ctx.renderer()?.kickShake(SHAKE_BOSS);
      ctx.audio.play(arenaBossCue());
    }
  });

  // One skill volley's whole FX cascade (fire-mage rework, ADR-0179/0181),
  // owner-only: the renderer plans the timed show (staggered arrow/fireball
  // flights, explosions and flame strikes landing at their hit's instant, chains
  // leaping off their parent's impact) and stages it on the FX overlay's
  // scheduler, damage floats included - skill hits send no CombatText. Hidden-tab
  // flushes spawn nothing (the rAF-driven layer would only age them late); unbaked
  // effect strips degrade to the Slice F primitives inside the overlay. The
  // volley's sounds ride its own staged instants (ADR-0187): `onEffect` per
  // projectile launch / explosion / flame-strike landing and `onHit` per struck
  // entity exactly when its damage float lands.
  ctx.events.on('arenaFx', (msg) => {
    if (arenaActive !== true) return;
    // Mirror the server's cast turn BEFORE the hidden-tab presentation gate
    // (ADR-0192): the authoritative run-world mobile already faced the volley, and
    // skipping the mirror on a hidden tab would leave the two facings divergent for
    // the walk gate. Idle-guarded inside faceCast. Prefer the authoritative
    // `castFacing` the server sends on a skill-fire volley - a straight shot emits
    // no projectile FX event (Slice D's FX inversion), so `volleyFacing(events)`
    // would find nothing and the walker would never mirror the turn (the "funky,
    // laggy" opening). Fall back to the events derivation only when the field is
    // absent (a ground-fx push).
    const castDir = msg.castFacing !== undefined ? msg.castFacing : volleyFacing(msg.events);
    if (castDir !== null) ctx.state.faceCast(castDir);
    if (ctx.ui.hidden) return;
    ctx.renderer()?.playVolleyFx(msg.events, ARENA_DEALT_COLOR, {
      onEffect: (effect) => {
        const now = performance.now();
        if (now - lastSpellFxMs < SPELL_FX_COOLDOWN_MS) return;
        const cue = spellCue(effect, SPELL_FX_VOLUME);
        if (cue) {
          lastSpellFxMs = now;
          ctx.audio.play(cue);
        }
      },
      onHit: (target, viaEffect) => {
        const world = ctx.world();
        const playerId = ctx.playerId();
        const struck = world?.get(target);
        // Blood on the runner's own volleys (PRD #372, Slice C): CLIENT-DERIVED, no
        // wire message. A maxed cascade is ~100 hits already batched into this one
        // ArenaFx push (ADR-0179); adding per-hit blood WIRE messages would undo
        // that batching, so the scatter is rolled locally off Math.random (a run is
        // single-viewer/private, so shared-view determinism does not matter) and
        // sprayed through the same `splatBlood` primitive the town wire path
        // drives. Firing off onHit times each splat to its damage float (staggered
        // per generation), so blood lands with the hit, not on launch. The
        // BloodLayer's 64-splat cap bounds the display list under the whole cascade.
        if (struck?.mobile) {
          const h = struck.health;
          const count = h ? bloodSplatCount(h.current, h.max) : 1;
          ctx.renderer()?.splatBlood(
            { x: struck.mobile.tileX, y: struck.mobile.tileY },
            rollBloodScatter(count, Math.random),
          );
        }
        const now = performance.now();
        // The archer's arrow/bolt impact thwack (ADR-0237): a physical shot lands a
        // thud on the struck enemy, proximity-scaled, throttled like the spell/hurt
        // cues (a Double-Shot/Ricochet volley lands several at once). Fired before
        // the hurt-grunt early-return so it plays regardless of it.
        if (viaEffect === 'arrow' || viaEffect === 'bolt') {
          if (struck?.mobile && playerId !== undefined && now - lastArrowHitMs >= SPELL_FX_COOLDOWN_MS) {
            const hitVol = proximityCueVolume(
              world,
              playerId,
              { x: struck.mobile.tileX, y: struck.mobile.tileY },
              SPELL_FX_VOLUME,
            );
            if (hitVol > 0) {
              lastArrowHitMs = now;
              ctx.audio.play({ sound: 'arrow-hit', volume: hitVol });
            }
          }
        }
        if (now - lastMonsterHurtMs < MONSTER_HURT_COOLDOWN_MS) return;
        if (!struck?.mobile) return; // dead-by-render-time target: no yelp
        const volume = proximityCueVolume(
          world,
          playerId,
          { x: struck.mobile.tileX, y: struck.mobile.tileY },
          MONSTER_HURT_VOLUME,
        );
        const cue = monsterCue(struck.renderable?.body, 'hurt', volume);
        if (cue) {
          lastMonsterHurtMs = now;
          ctx.audio.play(cue);
        }
      },
    });
  });

  ctx.events.on('actionAnimation', (msg) => {
    const world = ctx.world();
    const playerId = ctx.playerId();
    const source = world?.get(msg.entityId)?.renderable;
    // A bestiary monster's attack swing (ADR-0187): its sound family's one attack
    // cue, keyed off the replicated body like the vortex gust and proximity-scaled
    // the same way. The horde bite clips are the monster-band `attack-a/-b/-c`.
    if (isMonsterBody(source?.body) && msg.clip.startsWith('attack-')) {
      const now = performance.now();
      if (now - lastMonsterAttackMs >= MONSTER_ATTACK_COOLDOWN_MS) {
        const volume = proximityCueVolume(world, playerId, msg.entityId, MONSTER_ATTACK_VOLUME);
        const cue = monsterCue(source?.body, 'attack', volume);
        if (cue) {
          lastMonsterAttackMs = now;
          ctx.audio.play(cue);
        }
      }
    }
    // Arena juice off the same owner-only cues (Slice F, ADR-0178; volley FX ride
    // the ArenaFx cascade handler above, ADR-0179) - all gated on a live run so
    // town presentation (duels, swarms, storms) is untouched:
    //  - the runner's `hit` flinch (a survived horde bite) kicks the small shake;
    //  - an enemy `die` clip pops the pooled death burst where it fell - a boss
    //    (any of the rotating roster, ADR-0211) gets the big purple shower + the
    //    full shake.
    // Every effect is a renderer no-op on a missing sprite, and hidden-tab frames
    // spawn nothing (the rAF-driven FX layer would only age them late).
    if (arenaActive === true && !ctx.ui.hidden) {
      if (msg.clip === 'hit' && msg.entityId === playerId) {
        ctx.renderer()?.kickShake(SHAKE_HIT);
      }
      if (msg.clip === 'die' && msg.entityId !== playerId) {
        if (isArenaBossBody(source?.body)) {
          ctx.renderer()?.kickShake(SHAKE_BOSS);
          ctx.renderer()?.burstAtEntity(msg.entityId, ARENA_BOSS_BURST_COLOR, BOSS_BURST_PARTICLES);
        } else {
          ctx.renderer()?.burstAtEntity(msg.entityId, ARENA_BURST_COLOR, BURST_PARTICLES);
        }
      }
    }
    // Feed the kill-streak combo (PRD #432) off the same enemy die-clip signal the
    // death burst rides above - horde and boss bodies both count one each (the
    // boss's weight shows in the tier styling, not the arithmetic), and
    // zone/vortex/chain kills all surface through this one reaper->die path.
    // recordKill runs even on a HIDDEN tab so the machine stays truthful (the
    // counter catches up when the tab returns); the tier stinger is presentation,
    // so it is gated on a visible tab like the burst.
    if (arenaActive === true && msg.clip === 'die' && msg.entityId !== playerId) {
      const tierUp = combo.recordKill(performance.now());
      if (tierUp > 0 && !ctx.ui.hidden) ctx.audio.play(arenaComboStingerCue(tierUp));
    }
    // A bestiary monster's death cry (ADR-0187): the family's one die sound, played
    // as the die clip fires (the corpse lingers, so its tile is still in the world),
    // proximity-scaled like the rat squeal.
    if (msg.clip === 'die' && isMonsterBody(source?.body)) {
      const now = performance.now();
      if (now - lastMonsterDieMs >= MONSTER_DIE_COOLDOWN_MS) {
        const volume = proximityCueVolume(world, playerId, msg.entityId, MONSTER_DIE_VOLUME);
        const cue = monsterCue(source?.body, 'die', volume);
        if (cue) {
          lastMonsterDieMs = now;
          ctx.audio.play(cue);
        }
      }
    }
  });

  // The hurt vignette (hit-feedback PRD #430 Slice C, ADR-0224): the same
  // runner-damage beat that kicks SHAKE_HIT also pulses the red vignette here,
  // where the damage number is - peak scaled by damage / maxHp off the replicated
  // health component the client already has. The `entityId === playerId` gate is
  // LOAD-BEARING, not belt-and-suspenders: enemies also receive per-hit CombatText
  // in a run (the projectile floats), and only the runner's own bites may sting -
  // do not remove it. Arena-gated so town duel numbers stay vignette-free, and
  // skipped on hidden tabs like the rest of the run's presentation. (The damage
  // float itself is the duel installer's shared combatText handler.)
  ctx.events.on('combatText', (msg) => {
    if (msg.outcome !== 'damage') return;
    if (arenaActive === true && msg.entityId === ctx.playerId() && !ctx.ui.hidden) {
      const damage = Number(msg.text);
      const maxHp = ctx.world()?.get(msg.entityId)?.health?.max;
      if (Number.isFinite(damage) && damage > 0 && maxHp !== undefined && maxHp > 0) {
        arenaWarning.hurt(damage / maxHp);
      }
    }
  });

  ctx.onFrame(() => {
    // The Arena run HUD's survival clock (ADR-0177): server-anchored on each
    // owner-only status push, client-ticked here, frozen during a draft pause.
    // A no-op while no run is live.
    arenaHud.sync();

    // Age the elite/boss banner and ease the level-up draft veil (Slice F,
    // ADR-0178 / ADR-0218). The veil's target is simply "is the draft open" -
    // re-asserted every frame (last write wins), so it fades in as the draft shows
    // and fades out on every close path (pick, re-offer gap, run end, reconnect
    // reset) with no per-path wiring. Cheap no-ops while idle.
    arenaWarning.setVeil(arenaDraft.isOpen());
    arenaWarning.sync();

    // The low-HP heartbeat (Slice D, ADR-0227): poll the danger machine's fixed
    // throttle each frame and sound a due beat at its HP-ramped volume. A null
    // while out of danger or inside the window - and the rAF loop pauses on a
    // hidden tab, so a background run never thumps.
    const heartbeat = arenaDanger.beat();
    if (heartbeat !== null) ctx.audio.play(arenaHeartbeatCue(heartbeat));

    // The kill-streak combo counter (PRD #432): expire the chain on the render
    // clock, reconcile the counter (the climbing Nx, the draining window bar, the
    // tier styling), and play the hold-then-fall-away on the break edge. Runs
    // unconditionally (not arena-gated) so a chain left live at run end still ages
    // out and falls away cleanly; a cheap no-op while no chain is up. The rAF loop
    // pauses on a hidden tab, so a background run's counter simply catches up on
    // return (the machine kept counting truthfully off the die-clip stream).
    const comboNow = performance.now();
    // Freeze the streak's expiry clock while a level-up draft is open (the same
    // flag the HUD clock and warning veil pause on): the run is frozen server-side,
    // so ageing the combo on wall time would break a chain the player is only
    // pausing to pick an upgrade. Re-asserted every frame.
    combo.setPaused(arenaDraft.isOpen(), comboNow);
    const brokenCombo = combo.sync(comboNow);
    arenaCombo.render(combo.state(comboNow));
    if (brokenCombo > 0) arenaCombo.breakChain(brokenCombo);
  }, () => arenaActive === true);
}
